import React, { ReactNode } from 'react';
import { Alert, Card, Chip, Grid, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Check, CheckCircle, ErrorOutline, LockOutlined, Remove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { mb } from '../common/theme/mbTokens';

export type PaymentProvider = 'square' | 'stripe';
export type ProviderCardState = 'idle' | 'connected' | 'locked' | 'lost';
export type ProviderCardRow = {
  label: string;
  value: string;
  isMonospace?: boolean;
  link?: { label: string; href: string };
};

type ProviderCapability = { labelKey: string; isAvailable: boolean };

const STRIPE_LOGO_COLOR = '#635bff';

export const PROVIDER_NAMES: Record<PaymentProvider, string> = { square: 'Square', stripe: 'Stripe' };
const PROVIDER_LOGOS: Record<PaymentProvider, { text: string; color: string }> = {
  square: { text: 'Square', color: mb.ink },
  stripe: { text: 'stripe', color: STRIPE_LOGO_COLOR },
};
const PROVIDER_CAPABILITIES: Record<PaymentProvider, ProviderCapability[]> = {
  square: [
    { labelKey: 'payments.capability.cardPayments', isAvailable: true },
    { labelKey: 'payments.capability.savedCards', isAvailable: true },
    { labelKey: 'payments.capability.refunds', isAvailable: true },
    { labelKey: 'payments.capability.cardReaders', isAvailable: false },
  ],
  stripe: [
    { labelKey: 'payments.capability.cardPayments', isAvailable: true },
    { labelKey: 'payments.capability.savedCards', isAvailable: true },
    { labelKey: 'payments.capability.refunds', isAvailable: true },
    { labelKey: 'payments.capability.cardReaders', isAvailable: true },
  ],
};

const getChip = (state: ProviderCardState, isSandbox: boolean | undefined, t: (key: string) => string) => {
  if (state === 'lost') {
    return { label: t('payments.lost.chip'), background: mb.dangerSoft, color: mb.danger };
  }
  if (state !== 'connected') {
    return undefined;
  }
  return isSandbox
    ? { label: t('payments.sandbox'), background: mb.claySoft, color: mb.clayInk }
    : { label: t('payments.live'), background: mb.mint, color: mb.green };
};

type ProviderCardProps = {
  provider: PaymentProvider;
  state: ProviderCardState;
  isSandbox?: boolean;
  isTaxLocked?: boolean;
  lostDate?: string;
  rows?: ProviderCardRow[];
  actions?: ReactNode;
};

const ProviderLogo = ({ provider, isMuted }: { provider: PaymentProvider; isMuted?: boolean }) => (
  <Typography
    sx={{
      fontSize: 26,
      fontWeight: 700,
      letterSpacing: '-0.5px',
      direction: 'ltr',
      color: PROVIDER_LOGOS[provider].color,
      opacity: isMuted ? 0.55 : 1,
    }}>
    {PROVIDER_LOGOS[provider].text}
  </Typography>
);

export const ProviderCard = ({
  provider,
  state,
  isSandbox,
  isTaxLocked,
  lostDate,
  rows,
  actions,
}: ProviderCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const providerName = PROVIDER_NAMES[provider];
  const connectedProviderName = PROVIDER_NAMES[provider === 'square' ? 'stripe' : 'square'];
  const isLocked = state === 'locked';

  if (isLocked && isPhone) {
    return (
      <Grid
        container
        alignItems="center"
        flexWrap="nowrap"
        gap={1.5}
        sx={{
          p: 2,
          bgcolor: mb.surface,
          border: `1px solid ${mb.line}`,
          borderRadius: `${mb.radius.card}px`,
          color: mb.muted,
        }}>
        <LockOutlined fontSize="small" />
        <Typography variant="body2">
          <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
            {providerName}
          </Typography>
          {' · '}
          {t('payments.locked', { connected: connectedProviderName, provider: providerName })}
        </Typography>
      </Grid>
    );
  }

  const chip = getChip(state, isSandbox, t);
  const showCapabilities = state === 'idle' || state === 'connected';

  return (
    <Card
      sx={{
        height: '100%',
        boxSizing: 'border-box',
        p: mb.layout.cardPadding,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...(isLocked && { bgcolor: mb.ground, boxShadow: 'none' }),
      }}>
      <Grid container alignItems="center" justifyContent="space-between" flexWrap="nowrap" gap={1.5}>
        <ProviderLogo provider={provider} isMuted={isLocked} />
        {isLocked && <LockOutlined fontSize="small" sx={{ color: mb.muted }} />}
        {chip && (
          <Chip
            size="small"
            label={chip.label}
            sx={{ bgcolor: chip.background, color: chip.color, fontWeight: 500, borderRadius: `${mb.radius.chip}px` }}
          />
        )}
      </Grid>

      {(state === 'idle' || isLocked) && (
        <Grid container flexDirection="column" gap={0.5}>
          <Typography variant="h6" fontWeight={700} color={isLocked ? 'text.secondary' : undefined}>
            {t(`payments.${provider}.title`)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isLocked
              ? t('payments.locked', { connected: connectedProviderName, provider: providerName })
              : t('payments.providerDescription')}
          </Typography>
        </Grid>
      )}

      {state === 'connected' && (
        <Grid container alignItems="center" gap={1}>
          <CheckCircle color="success" />
          <Typography variant="h6" fontWeight={700}>
            {t('payments.connected', { provider: providerName })}
          </Typography>
        </Grid>
      )}

      {state === 'lost' && (
        <>
          <Grid container alignItems="center" gap={1}>
            <ErrorOutline color="error" />
            <Typography variant="h6" fontWeight={700}>
              {t('payments.lost.title')}
            </Typography>
          </Grid>
          <Typography variant="body2">{t('payments.lost.body', { date: lostDate })}</Typography>
        </>
      )}

      {state === 'connected' && isSandbox && (
        <Alert severity="warning">{t('payments.sandboxAlert', { provider: providerName })}</Alert>
      )}

      {rows && rows.length > 0 && (
        <Grid container flexDirection="column">
          {rows.map((row) => (
            <Grid
              key={row.label}
              container
              flexDirection={{ xs: 'column', sm: 'row' }}
              flexWrap="nowrap"
              gap={{ xs: 0.25, sm: 2 }}
              sx={{ py: 1.25, borderBottom: `1px solid ${mb.lineSoft}`, '&:last-of-type': { borderBottom: 0 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: { sm: '42%' }, flexShrink: 0 }}>
                {row.label}
              </Typography>
              <Typography
                variant="body2"
                sx={
                  row.isMonospace
                    ? { fontVariantNumeric: 'tabular-nums', direction: 'ltr', unicodeBidi: 'isolate' }
                    : {}
                }>
                {row.value}
                {row.link && (
                  <>
                    {row.value && <br />}
                    <Link href={row.link.href} target="_blank" rel="noopener noreferrer" variant="body2">
                      {row.link.label}
                    </Link>
                  </>
                )}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}

      {showCapabilities && (
        <Grid container flexDirection="column" gap={1}>
          {PROVIDER_CAPABILITIES[provider].map((capability) => (
            <Grid key={capability.labelKey} container alignItems="center" flexWrap="nowrap" gap={1}>
              {capability.isAvailable ? (
                <Check fontSize="small" color="success" />
              ) : (
                <Remove fontSize="small" sx={{ color: mb.muted }} />
              )}
              <Typography variant="body2" color={capability.isAvailable ? 'text.primary' : 'text.secondary'}>
                {capability.isAvailable
                  ? t(capability.labelKey)
                  : t('payments.capability.notYet', { capability: t(capability.labelKey) })}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}

      {state === 'connected' && provider === 'stripe' && isTaxLocked && (
        <Alert severity="info">{t('payments.taxLimits')}</Alert>
      )}

      {state === 'lost' && (
        <Typography variant="caption" color="text.secondary">
          {t('payments.lost.sameAccount')}
        </Typography>
      )}

      {actions && (
        <Grid
          container
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1}
          sx={{ mt: 'auto' }}>
          {actions}
        </Grid>
      )}

      {state === 'idle' && (
        <Typography variant="caption" color="text.secondary">
          {t('payments.connectHint', { provider: providerName })}
        </Typography>
      )}
    </Card>
  );
};
